import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

const s = {
  page: { maxWidth: 800, margin: '0 auto', padding: '40px 20px', color: 'var(--text)' },
  back: { color: 'var(--primary)', cursor: 'pointer', fontWeight: 600, fontSize: 15, marginBottom: 32, display: 'inline-block', textDecoration: 'none' },
  hero: { background: '#1e3a8a', borderRadius: 16, padding: '36px 40px', marginBottom: 40, color: '#fff' },
  heroTitle: { fontSize: 32, fontWeight: 800, marginBottom: 8 },
  heroSub: { fontSize: 15, opacity: 0.85 },
  section: { background: 'var(--surface)', borderRadius: 14, padding: '28px 32px', marginBottom: 20, boxShadow: 'var(--shadow)' },
  h2: { fontSize: 20, fontWeight: 700, color: 'var(--heading)', marginBottom: 14, marginTop: 0, display: 'flex', alignItems: 'center', gap: 10 },
  p: { color: 'var(--text2)', fontSize: 15, lineHeight: 1.7, marginBottom: 12, marginTop: 0 },
  ul: { color: 'var(--text2)', fontSize: 15, lineHeight: 1.8, paddingLeft: 22, marginBottom: 12, marginTop: 0 },
  warning: { background: 'var(--error-bg)', color: 'var(--error-text)', borderRadius: 10, padding: '14px 18px', fontSize: 14, fontWeight: 600, marginBottom: 20, borderLeft: '4px solid #ef4444' },
  highlight: { background: 'var(--primary-light)', color: 'var(--heading)', borderRadius: 10, padding: '14px 18px', fontSize: 14, fontWeight: 600, marginBottom: 20, borderLeft: '4px solid var(--primary)' },
  footer: { textAlign: 'center', color: 'var(--text4)', fontSize: 14, marginTop: 40, paddingTop: 24, borderTop: '1px solid var(--border)' },
  footerLink: { color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 },
};

export default function TermsOfService() {
  const navigate = useNavigate();
  const lastUpdated = 'March 15, 2026';

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <div style={s.page}>

        <span style={s.back} onClick={() => navigate(-1)}>← Go Back</span>

        <div style={s.hero}>
          <div style={s.heroTitle}>📜 Terms of Service</div>
          <div style={s.heroSub}>Last updated: {lastUpdated} · Please read carefully before using the app</div>
        </div>

        <div style={s.warning}>
          ⚠️ <strong>Not a Medical Device:</strong> SeniorCare is a care management and reminder tool only. It is NOT a substitute for professional medical advice, diagnosis, or treatment. Always consult a qualified healthcare provider for medical decisions.
        </div>

        {/* 1 */}
        <div style={s.section}>
          <h2 style={s.h2}>✅ 1. Acceptance of Terms</h2>
          <p style={s.p}>
            By creating an account or using SeniorCare ("the App", "the Service"), you agree to be bound by these Terms of Service and our <Link to="/privacy" style={{ color: 'var(--primary)' }}>Privacy Policy</Link>. If you do not agree, please do not use the App.
          </p>
          <p style={s.p}>
            You must be at least 18 years old to create an account. By using the App, you represent that you meet this requirement.
          </p>
        </div>

        {/* 2 */}
        <div style={s.section}>
          <h2 style={s.h2}>🏥 2. Description of Service</h2>
          <p style={s.p}>SeniorCare provides tools to help family members and caregivers manage care for elderly individuals, including:</p>
          <ul style={s.ul}>
            <li>Medication tracking and SMS reminder scheduling</li>
            <li>Medical appointment scheduling and calendar management</li>
            <li>Vital signs logging (blood pressure, heart rate, weight, blood glucose)</li>
            <li>Daily care journal entries</li>
            <li>SOS emergency SMS alerts</li>
            <li>PDF and email health reports</li>
            <li>Caregiver-to-senior assignment management</li>
          </ul>
          <div style={s.highlight}>
            💡 The App is a <strong>personal organiser</strong>. Medication reminders are sent as a convenience — they do not guarantee a dose will be taken. You remain responsible for actual medication administration and all medical decisions.
          </div>
        </div>

        {/* 3 */}
        <div style={s.section}>
          <h2 style={s.h2}>👤 3. User Accounts & Responsibilities</h2>
          <p style={s.p}>When you create an account, you agree to:</p>
          <ul style={s.ul}>
            <li>Provide accurate and complete registration information</li>
            <li>Verify your email address to activate your account</li>
            <li>Keep your password secure and not share it with others</li>
            <li>Be responsible for all activity that occurs under your account</li>
            <li>Notify us immediately if you suspect unauthorised access to your account</li>
          </ul>
          <p style={s.p}>
            You are solely responsible for the accuracy of all data you enter into the App, including medication names, dosages, and phone numbers for SMS alerts.
          </p>
        </div>

        {/* 4 */}
        <div style={s.section}>
          <h2 style={s.h2}>⛔ 4. Prohibited Uses</h2>
          <p style={s.p}>You agree not to:</p>
          <ul style={s.ul}>
            <li>Use the App for any unlawful purpose or in violation of any regulations</li>
            <li>Enter false or misleading information about seniors or caregivers</li>
            <li>Misuse the SOS feature (sending false emergency alerts)</li>
            <li>Attempt to reverse-engineer, hack, or disrupt the App or its servers</li>
            <li>Use the App to send spam or unsolicited communications</li>
            <li>Share your account with others or create accounts on behalf of another person without their consent</li>
            <li>Use the App to store or distribute content that is illegal, harmful, or offensive</li>
          </ul>
        </div>

        {/* 5 */}
        <div style={s.section}>
          <h2 style={s.h2}>💳 5. Subscription & Payments</h2>
          <p style={s.p}>
            SeniorCare offers both free and premium subscription tiers. Premium features (unlimited seniors, priority SMS reminders) require an active paid subscription processed through <strong>Stripe</strong>.
          </p>
          <ul style={s.ul}>
            <li>Subscriptions are billed on a recurring basis (monthly or annually as selected)</li>
            <li>You may cancel your subscription at any time through the Settings page</li>
            <li>Cancellation takes effect at the end of the current billing period</li>
            <li>We do not offer refunds for partial billing periods, except where required by law</li>
            <li>We reserve the right to change pricing with 30 days' notice</li>
          </ul>
          <p style={s.p}>
            Payment card data is processed directly by Stripe and never stored on our servers.
          </p>
        </div>

        {/* 6 */}
        <div style={s.section}>
          <h2 style={s.h2}>📊 6. Your Data & Content</h2>
          <p style={s.p}>
            You retain full ownership of all data you enter into the App (senior profiles, medications, vitals, journal entries, etc.). By using the App, you grant us a limited licence to store and process this data solely for the purpose of providing the Service.
          </p>
          <p style={s.p}>
            You can export your data at any time as a PDF report. You can delete individual records or request complete account deletion by contacting us.
          </p>
        </div>

        {/* 7 */}
        <div style={s.section}>
          <h2 style={s.h2}>🚨 7. SOS Feature — Important Notice</h2>
          <p style={s.p}>
            The SOS feature sends an SMS alert to caregivers linked to a senior profile. By using this feature you acknowledge:
          </p>
          <ul style={s.ul}>
            <li>This is <strong>not a substitute for calling emergency services</strong> (911, 999, 112, etc.)</li>
            <li>SMS delivery is not guaranteed and depends on mobile carrier availability</li>
            <li>We are not liable for delays or failures in SMS delivery during emergencies</li>
            <li>Misuse of the SOS feature (false alarms) may result in account suspension</li>
          </ul>
          <p style={s.p}><strong>In a life-threatening emergency, always call your local emergency services first.</strong></p>
        </div>

        {/* 8 */}
        <div style={s.section}>
          <h2 style={s.h2}>⚖️ 8. Disclaimers & Limitation of Liability</h2>
          <p style={s.p}>
            The App is provided "as is" and "as available" without warranties of any kind, either express or implied.
          </p>
          <p style={s.p}>We are not liable for:</p>
          <ul style={s.ul}>
            <li>Any missed medication doses or missed appointments, regardless of reminder failures</li>
            <li>Medical outcomes resulting from data entered into or generated by the App</li>
            <li>Errors or inaccuracies in data entered by users</li>
            <li>SMS or email delivery failures caused by third-party service providers</li>
            <li>Data loss due to device failure, browser clearing, or server issues</li>
            <li>Indirect, incidental, or consequential damages arising from use of the App</li>
          </ul>
          <p style={s.p}>
            To the maximum extent permitted by law, our total liability to you shall not exceed the amount you paid us in the 12 months preceding the claim.
          </p>
        </div>

        {/* 9 */}
        <div style={s.section}>
          <h2 style={s.h2}>🔚 9. Termination</h2>
          <p style={s.p}>
            You may stop using the App and delete your account at any time by contacting us at the email below.
          </p>
          <p style={s.p}>
            We reserve the right to suspend or terminate your account without notice if you violate these Terms, misuse the SOS feature, or engage in fraudulent activity. Upon termination, your data will be deleted within 30 days.
          </p>
        </div>

        {/* 10 */}
        <div style={s.section}>
          <h2 style={s.h2}>🔄 10. Changes to These Terms</h2>
          <p style={s.p}>
            We may update these Terms of Service from time to time. We will notify you of material changes by posting a notice in the App or via email at least 14 days before they take effect. Continued use of the App after changes constitutes your acceptance.
          </p>
        </div>

        {/* 11 */}
        <div style={s.section}>
          <h2 style={s.h2}>🌍 11. Governing Law</h2>
          <p style={s.p}>
            These Terms shall be governed by and construed in accordance with applicable law. Any disputes shall be resolved through good-faith negotiation first, followed by binding arbitration if necessary.
          </p>
        </div>

        {/* 12 */}
        <div style={s.section}>
          <h2 style={s.h2}>📧 12. Contact Us</h2>
          <p style={s.p}>
            If you have questions about these Terms, please contact us:
          </p>
          <ul style={s.ul}>
            <li><strong>App:</strong> SeniorCare</li>
            <li><strong>Email:</strong> legal@seniorcare.app</li>
          </ul>
          <p style={s.p}>
            For privacy-related requests, see our <Link to="/privacy" style={{ color: 'var(--primary)' }}>Privacy Policy</Link>.
          </p>
        </div>

        <div style={s.footer}>
          <p style={{ margin: '0 0 10px' }}>
            <Link to="/terms" style={s.footerLink}>Terms of Service</Link>
            {' · '}
            <Link to="/privacy" style={s.footerLink}>Privacy Policy</Link>
            {' · '}
            <Link to="/login" style={s.footerLink}>Sign In</Link>
          </p>
          <p style={{ margin: 0 }}>© {new Date().getFullYear()} SeniorCare. All rights reserved.</p>
        </div>

      </div>
    </div>
  );
}
